import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { useGuidedStepCardTransition } from '../../hooks';
import classes from './guided-step-card-swap.module.css';

const MotionPane = motion.div;

export type GuidedStepCardSwapProps = {
  children: ReactNode;
  /**
   * `1` when the active step index increases, `-1` when it decreases.
   * Passed as Framer `custom` for enter/exit variants.
   */
  direction: 1 | -1;
  /**
   * Stable id for the current step; becomes the React `key` on the motion pane so exits/enters run.
   */
  stepKey: string;
};

/**
 * `AnimatePresence` + motion pane for swapping guided-step main content.
 * Typical layout: {@link GuidedStepCardViewport} wraps this component; step bodies use {@link GuidedStepMain};
 * {@link GuidedStepFooter} stays a **sibling** of that viewport (not a child of this swap), so the footer does not animate with the card.
 */
export const GuidedStepCardSwap = ({ children, direction, stepKey }: GuidedStepCardSwapProps) => {
  const { transition, variants } = useGuidedStepCardTransition();

  return (
    <AnimatePresence custom={direction} initial={false} mode="sync">
      <MotionPane
        key={stepKey}
        className={classes.motionPane}
        custom={direction}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={transition}
        variants={variants}
      >
        {children}
      </MotionPane>
    </AnimatePresence>
  );
};
